'use client';
import ButtonBigCall from '@components/button-big-call/ButtonBigCall';
import { fetchFlats } from '@lib/store/slices/FilterSlice';
import { changeActiveRooms, changeRoomsAreas } from '@lib/store/slices/ProjectSlice';
import { SafeHtmlRenderer } from '@lib/tools/helpers/SafeHtmlRenderer';
import { useViewport } from '@lib/tools/hooks/useViewport';
import { animated, useSpring } from '@react-spring/web';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { A11y, EffectCreative } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import calcTheLeast from '../../../lib/tools/_calc-the-least';
import ParallaxMedia from '../../parallax-media/ParallaxMedia';

import style from './SeventhScreenGeneral.module.scss';

import 'swiper/css';
import 'swiper/css/effect-creative';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import classNames from 'classnames';

function SeventhScreenGeneralMobile() {
  const [swiper1, setSwiperInstance] = useState(null);
  const roomsInfo = useSelector(state => state.projectinfo.roomsInfo);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(null);

  const { isMobile, isDesktop, userAgent } = useViewport();
  const allFlats = useSelector(state => state.filters.allFlats);
  const router = useRouter();
  const pathname = usePathname();
  const svgRef = useRef(null);

  useEffect(() => {
    if (allFlats.length === 0) {
      dispatch(fetchFlats());
    } else {
      const roomsAreas = {
        0: 0,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
      };

      Object.keys(roomsAreas).forEach(key => {
        const filteredFlats = allFlats.filter(flat => {
          if (Number(key) === 0) {
            return flat.rooms === 'studio';
          }
          return flat.rooms === Number(key);
        });

        const leastFlatsArea = calcTheLeast(filteredFlats, 'area', 1000000);
        roomsAreas[key] = leastFlatsArea !== 1000000 ? leastFlatsArea : roomsAreas[key];
      });

      dispatch(changeRoomsAreas(roomsAreas));
    }
  }, [allFlats, dispatch]);

  useLayoutEffect(() => {
    if (roomsInfo) setActiveTab(roomsInfo.filter(item => item.active === true));
  }, [roomsInfo]);

  const [toggle2, setToggle2] = useState(false);

  const opacityStyle = useSpring({
    config: { duration: 400 },
    opacity: toggle2 ? 0 : 1,
    delay: 0,
  });

  useLayoutEffect(() => {
    if (activeTab) {
      setToggle2(() => true);
      setTimeout(() => {
        setToggle2(() => false);
      }, 400);
    }
  }, [activeTab]);

  const handleScroll = e => e?.target?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

  useLayoutEffect(() => {
    if (activeTab && swiper1) {
      swiper1.slideTo(activeTab[0].id, 1000);
    }
  }, [activeTab, swiper1]);

  useEffect(() => {
    if (swiper1) {
      setTimeout(() => {
        swiper1.init();
        swiper1.update();
        dispatch(changeActiveRooms(0));
      }, 100);
    }
  }, [swiper1, pathname]);

  return (
    <div data-scroll className={style.seventhScreenContainer} ref={svgRef} id='plans'>
      <div className={style.sevenScreenSvg} />
      <div
        data-scroll
        data-scroll-speed={isDesktop && userAgent !== 'iPad' && userAgent !== 'Android' ? '1' : '0.5'}
        className={style.seventhScreenTitle}
      >
        <p>Планировки для лучших </p>
        <p>семейных моментов</p>
      </div>
      <div className={style.seventhScreenWrapper}>
        <div className={style.seventhScreenBoxDescription}>
          <div className={style.seventhScreenTabMenu}>
            <div className={style.seventhScreenTabMenuWrapper}>
              {roomsInfo?.map(item => (
                <button
                  key={item.id}
                  value={item.id}
                  className={item.active === true ? style.seventhScreenTabActive : style.seventhScreenTab}
                  onClick={e => {
                    dispatch(changeActiveRooms(item.id));
                    handleScroll(e);
                  }}
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>
          {isMobile && (
            <div className={style.seventhScreenImgContainer}>
              {activeTab && (
                <Swiper
                  modules={[A11y, EffectCreative]}
                  effect='creative'
                  initialSlide={0}
                  longSwipesMs={800}
                  speed={800}
                  creativeEffect={{
                    prev: {
                      shadow: true,
                      translate: ['-20%', 0, -1],
                    },
                    next: {
                      translate: ['100%', 0, 0],
                    },
                  }}
                  spaceBetween={10}
                  slidesPerView={1}
                  onSlideChange={swiper => {
                    dispatch(changeActiveRooms(swiper.activeIndex));
                  }}
                  onSwiper={swiper => {
                    swiper.allowSlideNext = false;
                    swiper.allowSlidePrev = false;
                    setSwiperInstance(swiper);
                  }}
                  className={style.sevenScreenImageBoxSwiper}
                >
                  {roomsInfo?.map(slide => (
                    <SwiperSlide
                      key={slide.id}
                      index={slide.id}
                      className={classNames('seventhScreenGeneralSwiperSlide', `__${slide.id}`)}
                    >
                      <ParallaxMedia src375={slide.srcMob} name='image' imagePlug={slide.plug} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          )}
          <div className={style.seventhScreenDescriptionContainer}>
            {activeTab && !toggle2 && (
              <animated.div style={opacityStyle} className={style.seventhScreenDescription}>
                <SafeHtmlRenderer tag='div' content={activeTab[0].description || ''} />
              </animated.div>
            )}
          </div>
          {!isMobile && (
            <>
              <div className={style.seventhScreenInfo}>
                <p>От</p>
                {activeTab && !toggle2 && (
                  <animated.p style={opacityStyle} className={style.seventhScreenInfoArea}>
                    {activeTab[0].area} м<sup>2</sup>
                  </animated.p>
                )}
              </div>
              <ButtonBigCall
                text='Выбрать квартиру'
                className={style.seventhScreenBtn}
                handleFuction={() => {
                  router.push(`/list?rooms=${activeTab[0].id}`);
                }}
              />
            </>
          )}
          {isMobile && (
            <div className={style.seventhScreenInfo}>
              <div className={style.seventhScreenInfoBoxMobile}>
                <p>От</p>
                {activeTab && !toggle2 && (
                  <animated.p style={opacityStyle} className={style.seventhScreenInfoArea}>
                    {activeTab[0].area} м<sup>2</sup>
                  </animated.p>
                )}
              </div>

              <ButtonBigCall
                text='Выбрать квартиру'
                className={style.seventhScreenBtn}
                handleFuction={() => {
                  router.push(`/list?rooms=${activeTab[0].id}`);
                }}
              />
            </div>
          )}
          <p className={style.seventhScreenSubTextBottom}>
            Для бесшумного и чистого переезда предусмотрена предчистовая отделка.
          </p>
        </div>
        {!isMobile && (
          <div className={style.seventhScreenImgContainer}>
            {activeTab && (
              <Swiper
                modules={[A11y, EffectCreative]}
                effect='creative'
                initialSlide={0}
                longSwipesMs={800}
                speed={800}
                creativeEffect={{
                  prev: {
                    shadow: true,
                    translate: ['-20%', 0, -1],
                  },
                  next: {
                    translate: ['100%', 0, 0],
                  },
                }}
                spaceBetween={10}
                slidesPerView={1}
                onSlideChange={swiper => {
                  dispatch(changeActiveRooms(swiper.activeIndex));
                }}
                onSwiper={swiper => {
                  swiper.allowSlideNext = false;
                  swiper.allowSlidePrev = false;
                  setSwiperInstance(swiper);
                }}
                className={style.eighthScreenImageBox}
              >
                {roomsInfo?.map(slide => (
                  <SwiperSlide
                    key={slide.id}
                    index={slide.id}
                    className={classNames('seventhScreenGeneralSwiperSlide', `__${slide.id}`)}
                  >
                    <ParallaxMedia src={slide.src} name='image' imagePlug={slide.plug} />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SeventhScreenGeneralMobile;
